import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface FlowRun {
  readonly flowId: string;
  readonly flowName: string;
  readonly integrationType: string;
  readonly lastRunStatus: string | null;
  readonly lastRunAt: string | null;
}

const RUN_STATUSES = ['', 'SUCCESS', 'FAILED', 'RUNNING', 'PARTIAL'];

/**
 * GR L29.04 — Flow Runs (`/admin/esb/runs`). Operational view of the most recent
 * execution of every `esb integration flow`: its outcome and when it ran, so
 * operators can spot failing integrations. Filterable by last-run status.
 */
export function GrEsbRunsPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<FlowRun[]>([]);
  const [runStatus, setRunStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    apiRequest<{ data: FlowRun[] }>('/v1/eip/flows', { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token]);

  const visible = runStatus ? rows.filter((r) => r.lastRunStatus === runStatus) : rows;

  return (
    <main aria-label="esb-runs">
      <h1>{t('gr29.runs.title')}</h1>
      <div className="filters">
        <label>
          {t('gr29.runs.labelRunStatus')}
          <select aria-label="filter-run-status" value={runStatus} onChange={(e) => setRunStatus(e.target.value)}>
            {RUN_STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('gr29.runs.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('gr29.runs.loading')}</p> : (
        <>
          <p aria-label="run-count">{t('gr29.runs.count', { count: String(visible.length) })}</p>
          {visible.length === 0 ? <p aria-label="empty">{t('gr29.runs.empty')}</p> : (
            <table aria-label="run-table">
              <thead><tr><th>{t('gr29.runs.colFlow')}</th><th>{t('gr29.runs.colType')}</th><th>{t('gr29.runs.colLastRun')}</th><th>{t('gr29.runs.colWhen')}</th></tr></thead>
              <tbody>
                {visible.map((r) => (
                  <tr key={r.flowId}>
                    <td>{r.flowName}</td><td>{r.integrationType}</td>
                    <td>{r.lastRunStatus ?? '—'}</td><td>{at(r.lastRunAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </main>
  );
}
